import React, { useState, useEffect, useCallback } from 'react';
import { adminLogin, getDashboardStats, getAdminProducts, getAdminOrders, 
         createProduct, updateProduct, deleteProduct, updateOrderStatus } from '../utils/api';
import { toast } from 'react-toastify';
import { FiLogOut, FiPackage, FiShoppingBag, FiDollarSign, FiClock, 
         FiPlus, FiEdit, FiTrash2, FiX, FiImage, FiStar } from 'react-icons/fi';
import './Admin.css';

// Base URL for serving static files (images)
const getBaseUrl = () => {
  if (process.env.NODE_ENV === 'production') return '';
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  return apiUrl.replace(/\/api\/?$/, '');
};

const Admin = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  // Multiple images support
  const [imagePreviews, setImagePreviews] = useState([]);
  const [newImageFiles, setNewImageFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [productForm, setProductForm] = useState({
    name: '', description: '', price: '', category: 'dog-food', petType: 'dog',
    brand: '', weight: '', stock: '', featured: false,
    hasVariants: false, variants: []
  });

  const handleLogin = useCallback(async (e, savedUsername, savedPassword) => {
    if (e) e.preventDefault();
    const username = savedUsername || loginForm.username;
    const password = savedPassword || loginForm.password;
    
    try {
      await adminLogin(username, password);
      localStorage.setItem('adminUsername', username);
      localStorage.setItem('adminPassword', password);
      setIsLoggedIn(true);
      toast.success('Login successful');
    } catch (error) {
      toast.error('Invalid credentials');
      localStorage.removeItem('adminUsername');
      localStorage.removeItem('adminPassword');
    }
  }, [loginForm.username, loginForm.password]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === 'dashboard') {
        const data = await getDashboardStats();
        setStats(data.stats);
      } else if (activeTab === 'products') {
        const data = await getAdminProducts();
        setProducts(data.products || []);
      } else if (activeTab === 'orders') {
        const data = await getAdminOrders();
        setOrders(data.orders || []);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    const savedUsername = localStorage.getItem('adminUsername');
    const savedPassword = localStorage.getItem('adminPassword');
    if (savedUsername && savedPassword) {
      handleLogin(null, savedUsername, savedPassword);
    }
  }, [handleLogin]);

  useEffect(() => {
    if (isLoggedIn) {
      loadData();
    }
  }, [isLoggedIn, activeTab, loadData]);

  const handleLogout = () => {
    localStorage.removeItem('adminUsername');
    localStorage.removeItem('adminPassword');
    setIsLoggedIn(false);
    toast.info('Logged out successfully');
  };

  // Handle multiple image uploads
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const totalImages = existingImages.length + newImageFiles.length + files.length;
    
    if (totalImages > 10) {
      toast.error('Maximum 10 images allowed per product');
      return;
    }
    
    const validFiles = [];
    const newPreviews = [];
    
    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is larger than 5MB`);
        return;
      }
      validFiles.push(file);
      newPreviews.push({
        url: URL.createObjectURL(file),
        isNew: true,
        file: file
      });
    });
    
    setNewImageFiles(prev => [...prev, ...validFiles]);
    setImagePreviews(prev => [...prev, ...newPreviews]);
  };

  // Remove an image (both new and existing)
  const removeImage = (index) => {
    const imageToRemove = imagePreviews[index];
    
    if (imageToRemove.isNew) {
      // Remove from new files - find the correct index among new files
      const newFileIndex = imagePreviews.slice(0, index).filter(img => img.isNew).length;
      setNewImageFiles(prev => prev.filter((_, i) => i !== newFileIndex));
    } else {
      // Remove from existing images using the originalUrl (relative URL)
      const originalUrl = imageToRemove.originalUrl || imageToRemove.url;
      setExistingImages(prev => prev.filter(img => img.url !== originalUrl));
    }
    
    // Remove from previews
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  // Set an image as primary
  const setPrimaryImage = (index) => {
    setImagePreviews(prev => prev.map((img, i) => ({
      ...img,
      isPrimary: i === index
    })));
    
    // Update existing images if it's not a new image
    const targetImage = imagePreviews[index];
    if (!targetImage.isNew) {
      const originalUrl = targetImage.originalUrl || targetImage.url;
      setExistingImages(prev => prev.map(img => ({
        ...img,
        isPrimary: img.url === originalUrl
      })));
    }
  };

  // Variant management functions
  const addVariant = () => {
    setProductForm({
      ...productForm,
      variants: [...productForm.variants, { weight: '', price: '', stock: '', sku: '' }]
    });
  };

  const updateVariant = (index, field, value) => {
    const updatedVariants = [...productForm.variants];
    updatedVariants[index] = { ...updatedVariants[index], [field]: value };
    setProductForm({ ...productForm, variants: updatedVariants });
  };

  const removeVariant = (index) => {
    const updatedVariants = productForm.variants.filter((_, i) => i !== index);
    setProductForm({ ...productForm, variants: updatedVariants });
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = { ...productForm };
      
      // If hasVariants is enabled, validate variants
      if (formData.hasVariants && formData.variants.length === 0) {
        toast.error('Please add at least one variant');
        return;
      }

      // Add new image files
      if (newImageFiles.length > 0) {
        formData.images = newImageFiles;
      }
      
      // Add existing images to keep (with updated isPrimary flags)
      const updatedExistingImages = existingImages.map(img => {
        // Find matching preview using originalUrl
        const preview = imagePreviews.find(p => !p.isNew && (p.originalUrl === img.url || p.url === img.url));
        return {
          url: img.url,
          alt: img.alt || productForm.name,
          isPrimary: preview?.isPrimary || img.isPrimary
        };
      });
      
      if (updatedExistingImages.length > 0) {
        formData.existingImages = updatedExistingImages;
      }

      if (editingProduct) {
        await updateProduct(editingProduct._id, formData);
        toast.success('Product updated successfully');
      } else {
        await createProduct(formData);
        toast.success('Product created successfully');
      }
      setShowProductModal(false);
      setEditingProduct(null);
      resetProductForm();
      loadData();
    } catch (error) {
      toast.error('Failed to save product');
    }
  };

  const handleDeleteProduct = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await deleteProduct(id);
        toast.success('Product deleted successfully');
        loadData();
      } catch (error) {
        toast.error('Failed to delete product');
      }
    }
  };

  const handleOrderStatusUpdate = async (orderId, newStatus) => {
    try {
      await updateOrderStatus(orderId, newStatus);
      toast.success('Order status updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update order status');
    }
  };

  const resetProductForm = () => {
    setProductForm({
      name: '', description: '', price: '', category: 'dog-food', petType: 'dog',
      brand: '', weight: '', stock: '', featured: false,
      hasVariants: false, variants: []
    });
    setImagePreviews([]);
    setNewImageFiles([]);
    setExistingImages([]);
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setProductForm({
      ...product,
      hasVariants: product.hasVariants || false,
      variants: product.variants || []
    });
    
    // Load existing images
    const existingImgs = [];
    const previews = [];
    
    if (product.images && product.images.length > 0) {
      product.images.forEach((img, index) => {
        const displayUrl = img.url.startsWith('/uploads/') 
          ? `${getBaseUrl()}${img.url}` 
          : img.url;
        existingImgs.push({
          url: img.url, // Store relative URL
          alt: img.alt || product.name,
          isPrimary: img.isPrimary || index === 0
        });
        previews.push({
          url: displayUrl, // Full URL for display
          originalUrl: img.url, // Store original relative URL for matching
          isPrimary: img.isPrimary || index === 0,
          isNew: false
        });
      });
    } else if (product.imageUrl) {
      // Fallback to single imageUrl
      const displayUrl = product.imageUrl.startsWith('/uploads/') 
        ? `${getBaseUrl()}${product.imageUrl}` 
        : product.imageUrl;
      existingImgs.push({
        url: product.imageUrl, // Store relative URL
        alt: product.name,
        isPrimary: true
      });
      previews.push({
        url: displayUrl, // Full URL for display
        originalUrl: product.imageUrl, // Store original relative URL for matching
        isPrimary: true,
        isNew: false
      });
    }
    
    setExistingImages(existingImgs);
    setImagePreviews(previews);
    setNewImageFiles([]);
    setShowProductModal(true);
  };

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return 'https://via.placeholder.com/50x50?text=No+Img';
    if (imageUrl.startsWith('/uploads/')) {
      return `${getBaseUrl()}${imageUrl}`;
    }
    return imageUrl;
  };

  if (!isLoggedIn) {
    return (
      <div className="admin-login-page">
        <div className="login-card">
          <div className="login-header">
            <h1>Admin Portal</h1>
            <p>Sign in to manage your store</p>
          </div>
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label>Username</label>
              <input
                type="text"
                value={loginForm.username}
                onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                placeholder="Enter username"
                required
              />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                placeholder="Enter password"
                required
              />
            </div>
            <button type="submit" className="btn btn-primary login-btn">
              Sign In
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <aside className="admin-sidebar">
        <div className="admin-logo">
          <span className="logo-text">PAWKIT</span>
          <span className="logo-sub">Admin</span>
        </div>
        <nav className="admin-nav">
          <button 
            className={activeTab === 'dashboard' ? 'active' : ''}
            onClick={() => setActiveTab('dashboard')}
          >
            <FiShoppingBag /> Dashboard
          </button>
          <button 
            className={activeTab === 'products' ? 'active' : ''}
            onClick={() => setActiveTab('products')}
          >
            <FiPackage /> Products
          </button>
          <button 
            className={activeTab === 'orders' ? 'active' : ''}
            onClick={() => setActiveTab('orders')}
          >
            <FiClock /> Orders
          </button>
        </nav>
        <button onClick={handleLogout} className="logout-btn">
          <FiLogOut /> Sign Out
        </button>
      </aside>

      <main className="admin-content">
        {loading && (
          <div className="loading-overlay">
            <div className="spinner"></div>
          </div>
        )}

        {activeTab === 'dashboard' && stats && (
          <div className="dashboard-section">
            <h1>Dashboard</h1>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon products">
                  <FiPackage />
                </div>
                <div className="stat-info">
                  <h3>Total Products</h3>
                  <p className="stat-value">{stats.totalProducts}</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon orders">
                  <FiShoppingBag />
                </div>
                <div className="stat-info">
                  <h3>Total Orders</h3>
                  <p className="stat-value">{stats.totalOrders}</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon pending">
                  <FiClock />
                </div>
                <div className="stat-info">
                  <h3>Pending Orders</h3>
                  <p className="stat-value">{stats.pendingOrders}</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon revenue">
                  <FiDollarSign />
                </div>
                <div className="stat-info">
                  <h3>Total Revenue</h3>
                  <p className="stat-value">₹{stats.totalRevenue.toLocaleString()}</p>
                </div>
              </div>
            </div>

            <div className="recent-section">
              <h2>Recent Orders</h2>
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Order #</th>
                      <th>Customer</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recentOrders && stats.recentOrders.map(order => (
                      <tr key={order._id}>
                        <td><code>{order.orderNumber}</code></td>
                        <td>{order.customerInfo.name}</td>
                        <td>₹{order.totalAmount.toLocaleString()}</td>
                        <td><span className={`status-badge ${order.orderStatus}`}>{order.orderStatus}</span></td>
                        <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'products' && (
          <div className="products-section">
            <div className="section-header">
              <h1>Products</h1>
              <button 
                className="btn btn-primary"
                onClick={() => {
                  resetProductForm();
                  setEditingProduct(null);
                  setShowProductModal(true);
                }}
              >
                <FiPlus /> Add Product
              </button>
            </div>

            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Name</th>
                    <th>Category</th>
                    <th>Price</th>
                    <th>Stock</th>
                    <th>Variants</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map(product => (
                    <tr key={product._id}>
                      <td>
                        <img 
                          src={getImageUrl(product.imageUrl)} 
                          alt={product.name} 
                          className="product-thumb" 
                        />
                      </td>
                      <td>
                        <strong>{product.name}</strong>
                      </td>
                      <td>{product.category}</td>
                      <td>
                        {product.hasVariants && product.variants?.length > 0 
                          ? `₹${Math.min(...product.variants.map(v => v.price)).toLocaleString()}+`
                          : `₹${product.price.toLocaleString()}`
                        }
                      </td>
                      <td>
                        {product.hasVariants && product.variants?.length > 0
                          ? product.variants.reduce((sum, v) => sum + v.stock, 0)
                          : product.stock
                        }
                      </td>
                      <td>
                        {product.hasVariants && product.variants?.length > 0
                          ? <span className="text-sm">{product.variants.length} sizes</span>
                          : <span className="text-sm text-gray">None</span>
                        }
                      </td>
                      <td>
                        <span className={`status-badge ${product.isActive ? 'active' : 'inactive'}`}>
                          {product.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button onClick={() => openEditModal(product)} className="icon-btn edit">
                            <FiEdit />
                          </button>
                          <button onClick={() => handleDeleteProduct(product._id)} className="icon-btn delete">
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'orders' && (
          <div className="orders-section">
            <h1>Orders</h1>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Order #</th>
                    <th>Customer</th>
                    <th>Items</th>
                    <th>Amount</th>
                    <th>Payment</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(order => (
                    <tr key={order._id}>
                      <td><code>{order.orderNumber}</code></td>
                      <td>
                        <div>{order.customerInfo.name}</div>
                        <div className="text-sm text-gray">{order.customerInfo.email}</div>
                      </td>
                      <td>{order.items.length} items</td>
                      <td>₹{order.totalAmount.toLocaleString()}</td>
                      <td>
                        <span className={`status-badge ${order.paymentInfo.paymentStatus}`}>
                          {order.paymentInfo.paymentStatus}
                        </span>
                      </td>
                      <td>
                        <select 
                          value={order.orderStatus}
                          onChange={(e) => handleOrderStatusUpdate(order._id, e.target.value)}
                          className="status-select"
                        >
                          <option value="pending">Pending</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="processing">Processing</option>
                          <option value="shipped">Shipped</option>
                          <option value="delivered">Delivered</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </td>
                      <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* Product Modal */}
      {showProductModal && (
        <div className="modal-overlay" onClick={() => setShowProductModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingProduct ? 'Edit Product' : 'Add Product'}</h2>
              <button onClick={() => setShowProductModal(false)} className="close-btn">
                <FiX />
              </button>
            </div>
            <form onSubmit={handleProductSubmit} className="product-form">
              <div className="form-grid">
                {/* Multiple Images Upload */}
                <div className="form-group full-width">
                  <label>Product Images <span className="label-hint">(Up to 10 images, first is primary)</span></label>
                  
                  {/* Image Previews Grid */}
                  {imagePreviews.length > 0 && (
                    <div className="images-preview-grid">
                      {imagePreviews.map((img, index) => (
                        <div key={index} className={`image-preview-item ${img.isPrimary ? 'primary' : ''}`}>
                          <img src={img.url} alt={`Preview ${index + 1}`} />
                          <div className="image-actions">
                            <button 
                              type="button" 
                              onClick={() => setPrimaryImage(index)}
                              className={`btn-set-primary ${img.isPrimary ? 'active' : ''}`}
                              title={img.isPrimary ? 'Primary image' : 'Set as primary'}
                            >
                              <FiStar />
                            </button>
                            <button 
                              type="button" 
                              onClick={() => removeImage(index)}
                              className="btn-remove-image"
                              title="Remove image"
                            >
                              <FiX />
                            </button>
                          </div>
                          {img.isPrimary && <span className="primary-badge">Primary</span>}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Add More Images Button */}
                  {imagePreviews.length < 10 && (
                    <div className="image-upload-area">
                      <input 
                        type="file" 
                        id="productImages"
                        accept="image/*"
                        multiple
                        onChange={handleImageChange}
                        className="image-input"
                      />
                      <label htmlFor="productImages" className="image-upload-label add-more">
                        <div className="upload-placeholder">
                          <FiImage size={24} />
                          <span>{imagePreviews.length === 0 ? 'Click to upload images' : 'Add more images'}</span>
                          <span className="upload-hint">PNG, JPG, WebP up to 5MB each</span>
                        </div>
                      </label>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>Name *</label>
                  <input type="text" value={productForm.name} 
                    onChange={(e) => setProductForm({...productForm, name: e.target.value})}
                    required />
                </div>
                <div className="form-group">
                  <label>Brand *</label>
                  <input type="text" value={productForm.brand} 
                    onChange={(e) => setProductForm({...productForm, brand: e.target.value})}
                    required />
                </div>
                <div className="form-group full-width">
                  <label>Description *</label>
                  <textarea value={productForm.description} 
                    onChange={(e) => setProductForm({...productForm, description: e.target.value})}
                    required rows="3" />
                </div>
                
                <div className="form-group">
                  <label>Pet Type *</label>
                  <select value={productForm.petType} 
                    onChange={(e) => setProductForm({...productForm, petType: e.target.value})}>
                    <option value="dog">Dog</option>
                    <option value="cat">Cat</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Category *</label>
                  <select value={productForm.category} 
                    onChange={(e) => setProductForm({...productForm, category: e.target.value})}>
                    <option value="dog-food">Dog Food</option>
                    <option value="cat-food">Cat Food</option>
                    <option value="dog-treats">Dog Treats</option>
                    <option value="cat-treats">Cat Treats</option>
                    <option value="dog-accessories">Dog Accessories</option>
                    <option value="cat-accessories">Cat Accessories</option>
                  </select>
                </div>

                {/* Variants Toggle */}
                <div className="form-group full-width">
                  <label className="checkbox-label variant-toggle">
                    <input 
                      type="checkbox" 
                      checked={productForm.hasVariants} 
                      onChange={(e) => setProductForm({...productForm, hasVariants: e.target.checked})} 
                    />
                    <span>This product has multiple sizes/variants (e.g., 3kg, 7kg, 10kg)</span>
                  </label>
                </div>

                {/* Show base fields if no variants */}
                {!productForm.hasVariants && (
                  <>
                    <div className="form-group">
                      <label>Price (₹) *</label>
                      <input type="number" value={productForm.price} 
                        onChange={(e) => setProductForm({...productForm, price: e.target.value})}
                        required min="0" step="0.01" />
                    </div>
                    <div className="form-group">
                      <label>Stock *</label>
                      <input type="number" value={productForm.stock} 
                        onChange={(e) => setProductForm({...productForm, stock: e.target.value})}
                        required min="0" />
                    </div>
                    <div className="form-group">
                      <label>Weight *</label>
                      <input type="text" value={productForm.weight} 
                        onChange={(e) => setProductForm({...productForm, weight: e.target.value})}
                        placeholder="e.g., 1kg, 500g" required />
                    </div>
                  </>
                )}

                {/* Variants Section */}
                {productForm.hasVariants && (
                  <div className="form-group full-width variants-section">
                    <div className="variants-header">
                      <label>Product Variants</label>
                      <button type="button" onClick={addVariant} className="btn-add-variant">
                        <FiPlus /> Add Size
                      </button>
                    </div>
                    
                    {productForm.variants.length === 0 && (
                      <p className="variants-hint">Add different sizes like 3kg, 7kg, 10kg, 20kg with their respective prices and stock.</p>
                    )}

                    <div className="variants-list">
                      {productForm.variants.map((variant, index) => (
                        <div key={index} className="variant-row">
                          <div className="variant-field">
                            <label>Size/Weight</label>
                            <input 
                              type="text" 
                              value={variant.weight}
                              onChange={(e) => updateVariant(index, 'weight', e.target.value)}
                              placeholder="e.g., 3kg"
                              required
                            />
                          </div>
                          <div className="variant-field">
                            <label>Price (₹)</label>
                            <input 
                              type="number" 
                              value={variant.price}
                              onChange={(e) => updateVariant(index, 'price', e.target.value)}
                              placeholder="0"
                              min="0"
                              required
                            />
                          </div>
                          <div className="variant-field">
                            <label>Stock</label>
                            <input 
                              type="number" 
                              value={variant.stock}
                              onChange={(e) => updateVariant(index, 'stock', e.target.value)}
                              placeholder="0"
                              min="0"
                              required
                            />
                          </div>
                          <button 
                            type="button" 
                            onClick={() => removeVariant(index)}
                            className="btn-remove-variant"
                            aria-label="Remove variant"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label className="checkbox-label">
                    <input type="checkbox" checked={productForm.featured} 
                      onChange={(e) => setProductForm({...productForm, featured: e.target.checked})} />
                    <span>Featured Product</span>
                  </label>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowProductModal(false)} className="btn btn-outline">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingProduct ? 'Update' : 'Create'} Product
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
